import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Box,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tab,
  Tabs,
  FormHelperText,
} from '@mui/material';
import { CloudUpload, ExpandMore } from '@mui/icons-material';
import SegmentIcon from '@mui/icons-material/Segment';

import WrestlerTable from './WrestlerTable';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export interface Wrestler {
  id: string;
  firstName: string;
  lastName: string;
  weightKg: number;
  age?: number;
  grade?: string;
  gender?: string;
}

interface CsvImportProps {
  wrestlers: Wrestler[];
  schoolName: string;
  weightUnit: 'kg' | 'lbs';
  loading: boolean;
  error: string | null;
  success: string | null;
  submitted: boolean;
  submitting: boolean;
  allSubmittedWrestlers: Array<Wrestler & { schoolName: string }>;
  rawWeights: number[];
  csvData: any[];
  detectedColumns: string[];
  columnMappings: Record<string, string>;
  showColumnMapping: boolean;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPasteData: (data: string) => void;
  onSchoolNameChange: (name: string) => void;
  onWeightUnitChange: (unit: 'kg' | 'lbs') => void;
  onSubmit: () => void;
  onReset: () => void;
  onColumnMappingChange: (field: string, column: string) => void;
  onColumnMappingConfirm: (nameFormats?: Record<string, string>) => void;
  onColumnMappingCancel: () => void;
}

const CsvImport: React.FC<CsvImportProps> = ({
  wrestlers,
  schoolName,
  weightUnit,
  loading,
  error,
  success,
  submitted,
  submitting,
  allSubmittedWrestlers,
  rawWeights,
  csvData,
  detectedColumns,
  columnMappings,
  showColumnMapping,
  onFileUpload,
  onPasteData,
  onSchoolNameChange,
  onWeightUnitChange,
  onSubmit,
  onReset,
  onColumnMappingChange,
  onColumnMappingConfirm,
  onColumnMappingCancel,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [nameFormats, setNameFormats] = useState<Record<string, string>>({});

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFormatChange = (fieldKey: string, format: string) => {
    setNameFormats(prev => ({
      ...prev,
      [fieldKey]: format
    }));
  };

  const parseNameByFormat = (fullName: string, format: string, fieldKey: string): string => {
    if (format === 'all') return fullName;
    
    const trimmedName = fullName.trim();
    
    // Handle comma-separated names (Last, First)
    if (trimmedName.includes(',')) {
      const parts = trimmedName.split(',').map(part => part.trim());
      if (parts.length >= 2) {
        const lastName = parts[0];
        const firstName = parts[1];
        
        if (format === 'firstName') return fieldKey === 'firstName' ? firstName : '';
        if (format === 'lastName') return lastName; // Return last name for any field
        if (format === 'firstNameComma') return fieldKey === 'firstName' ? firstName : '';
        if (format === 'lastNameComma') return fieldKey === 'lastName' ? lastName : '';
      }
    }
    // Handle space-separated names (First Last)
    else {
      const parts = trimmedName.split(/\s+/);
      if (parts.length >= 2) {
        const firstName = parts[0];
        const lastName = parts[parts.length - 1]; // Take last word as last name
        
        if (format === 'firstName') return fieldKey === 'firstName' ? firstName : '';
        if (format === 'lastName') return lastName; // Return last name for any field
        if (format === 'firstNameComma') return ''; // No comma present
        if (format === 'lastNameComma') return ''; // No comma present
      }
    }
    
    return fullName; // Fallback
  };

  const renderFormatSelect = (fieldKey: string) => {
    switch(fieldKey){
      case 'firstName': case 'lastName':
        return (
          <Select 
            IconComponent={SegmentIcon}
            value={nameFormats[fieldKey] || 'all'}
            onChange={(e) => handleFormatChange(fieldKey, e.target.value)}
            size="small"
            sx={{ minWidth: 120, height: '3.5rem' }}
          >
            <MenuItem value="all">
              <Typography sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}><b>ALL</b></Typography>
            </MenuItem>
            <MenuItem value="firstName"><Typography sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}><b>FIRST</b> Last</Typography></MenuItem>
            <MenuItem value="lastName"><Typography sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>First <b>LAST</b></Typography></MenuItem>
            <MenuItem value="firstNameComma"><Typography sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>last, <b>FIRST</b></Typography></MenuItem>
            <MenuItem value="lastNameComma"><Typography sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}><b>LAST</b>, first</Typography></MenuItem>
          </Select>
        );
      default:
        return null;
    }
  };

  
  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Import Wrestler Data
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Upload CSV files to import wrestler data into the tournament system.
      </Typography>

      <Box sx={{ mt: 4 }}>
        {!submitted && wrestlers.length === 0 && (
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#FFFFFF', border: '1px solid #E0E0E0' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#212121' }}>
              Import Wrestlers
            </Typography>
        
        <Box sx={{ borderBottom: '1px solid #E0E0E0', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant="fullWidth">
            <Tab label="CSV" />
            <Tab label="Copy-Paste" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="body2" sx={{ color: '#757575', mb: 2 }}>
            CSV should include columns: first name, last name, weight (kg), and either age or grade
          </Typography>

          <Button
            variant="contained"
            component="label"
            startIcon={<CloudUpload />}
            disabled={loading}
          >
            Upload CSV
            <input
              type="file"
              accept=".csv"
              hidden
              onChange={onFileUpload}
            />
          </Button>
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <TextField
            label="Paste data here"
            multiline
            rows={10}
            fullWidth
            onChange={(event) => onPasteData(event.target.value)}
          />
        </TabPanel>

            {loading && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography>Processing CSV...</Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {success}
              </Alert>
            )}
          </Paper>
        )}

        {showColumnMapping && (
          <>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Map CSV Columns to Wrestler Fields</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Paper sx={{ p: 3, mb: 3, backgroundColor: '#FFFFFF', border: '1px solid #E0E0E0' }}>
                  <Typography variant="body2" sx={{ color: '#757575', mb: 3 }}>
                    We detected {detectedColumns.length} columns in your CSV. Please map them to the required wrestler fields.
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {[
                      { key: 'firstName', label: 'First Name', required: true },
                      { key: 'lastName', label: 'Last Name', required: false },
                      { key: 'weight', label: 'Weight', required: true },
                      { key: 'age', label: 'Age/Grade/Level', required: false },
                      { key: 'grade', label: 'Grade', required: false },
                      { key: 'gender', label: 'Gender', required: false },
                    ].map((field) => (
                      <Grid item xs={12} sm={6} key={field.key}>
                        <FormControl fullWidth>
                          
                          <Box display="flex" alignItems="center" gap={1}>
                            <InputLabel required={field.required}>
                              {field.label} {field.required && '*'}
                            </InputLabel>
                            <Select
                              value={columnMappings[field.key] || ''}
                              label={`${field.label} ${field.required && '*'}`}
                              onChange={(e) => onColumnMappingChange(field.key, e.target.value)}
                              sx={{ flexGrow: 1 }}
                            >
                              <MenuItem value="">-- Don't Import --</MenuItem>
                              {detectedColumns.map((column) => (
                                <MenuItem key={column} value={column}>
                                  {column}
                                </MenuItem>
                              ))}
                            </Select> 
                            {renderFormatSelect(field.key)}                         
                          </Box>
                        
                        </FormControl>
                      </Grid>
                    ))}
                  </Grid>

                </Paper>
              </AccordionDetails>
            </Accordion>

            {/* Preview section - always visible */}
            <Paper sx={{ p: 3, mb: 3, backgroundColor: '#FFFFFF', border: '1px solid #E0E0E0' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#212121' }}>
                Preview (First 3 Rows)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>First Name *</TableCell>
                      <TableCell>Last Name</TableCell>
                      <TableCell>Weight *</TableCell>
                      <TableCell>Age/Grade/Level</TableCell>
                      <TableCell>Grade</TableCell>
                      <TableCell>Gender</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvData.slice(0, 3).map((row, index) => {
                      const firstNameRaw = row[columnMappings.firstName] || '';
                      const lastNameRaw = row[columnMappings.lastName] || '';
                      const firstNameFormat = nameFormats.firstName || 'all';
                      const lastNameFormat = nameFormats.lastName || 'all';
                      
                      const firstName = parseNameByFormat(firstNameRaw, firstNameFormat, 'firstName');
                      const lastName = parseNameByFormat(lastNameRaw, lastNameFormat, 'lastName');
                      
                      return (
                        <TableRow key={index}>
                          <TableCell>{firstName || '-'}</TableCell>
                          <TableCell>{lastName || '-'}</TableCell>
                          <TableCell>{row[columnMappings.weight] || '-'}</TableCell>
                          <TableCell>{row[columnMappings.age] || '-'}</TableCell>
                          <TableCell>{row[columnMappings.grade] || '-'}</TableCell>
                          <TableCell>{row[columnMappings.gender] || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Action buttons moved here */}
              {![
                { key: 'firstName', label: 'First Name', required: true },
                { key: 'lastName', label: 'Last Name', required: false },
                { key: 'weight', label: 'Weight', required: true },
                { key: 'age', label: 'Age/Grade/Level', required: false },
                { key: 'grade', label: 'Grade', required: false },
                { key: 'gender', label: 'Gender', required: false },
              ].filter(field => field.required).every(field => columnMappings[field.key]) && (
                <Alert severity="warning" sx={{ mb: 2, mt: 2 }}>
                  Please map all required fields (marked with *) before continuing.
                </Alert>
              )}

              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item>
                  <Button
                    variant="contained"
                    onClick={() => onColumnMappingConfirm(nameFormats)}
                    disabled={![
                      { key: 'firstName', label: 'First Name', required: true },
                      { key: 'lastName', label: 'Last Name', required: false },
                      { key: 'weight', label: 'Weight', required: true },
                      { key: 'age', label: 'Age/Grade/Level', required: false },
                      { key: 'grade', label: 'Grade', required: false },
                      { key: 'gender', label: 'Gender', required: false },
                    ].filter(field => field.required).every(field => columnMappings[field.key])}
                  >
                    Finalize Submission
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={onColumnMappingCancel}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}

        {wrestlers.length > 0 && !submitted && (
          <>
            <Paper sx={{ p: 3, mb: 3, backgroundColor: '#FFFFFF', border: '1px solid #E0E0E0' }}>
              <WrestlerTable wrestlers={wrestlers} />
              <br></br>
              
              <Typography variant="h6" gutterBottom sx={{ color: '#212121' }}>
                Confirm Submission
              </Typography>
              
              <TextField
                label={schoolName ? "Team Name" : "⚠️ Team Name"}
                value={schoolName}
                onChange={(e) => onSchoolNameChange(e.target.value)}
                required={true}
                fullWidth
                sx={{ mb: 2 }}
                helperText="Team name extracted from filename (editable)"
              />
              
              {allSubmittedWrestlers.some(w => w.schoolName === schoolName) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This will add wrestlers to the existing {schoolName} team. 
                  Current team has {allSubmittedWrestlers.filter(w => w.schoolName === schoolName).length} wrestlers.
                </Alert>
              )}
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel >Import Weight Unit</InputLabel>
                <Select
                  value={weightUnit}
                  label="Weight Unit"
                  onChange={(e) => onWeightUnitChange(e.target.value as 'kg' | 'lbs')}
                >
                  <MenuItem value="kg">Kilograms (kg)</MenuItem>
                  <MenuItem value="lbs">Pounds (lbs)</MenuItem>
                </Select>
                <FormHelperText>Select the weight unit being imported for the wrestlers. All weights will be converted to kilograms for storage.</FormHelperText>
              </FormControl>

              {(rawWeights.some(w => w > 100) && weightUnit === 'kg') && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Some wrestlers appear to be over 100kg. If these weights are actually in pounds, please switch the weight unit to "Pounds (lbs)" above.
                </Alert>
              )}

              {schoolName ? "" : (<Alert severity="warning" sx={{ mb: 2 }}>
                Submitted Team needs a name (School/Club/Team)
                
              </Alert>)}
            

              <Typography variant="body1" sx={{ mb: 2, color: '#212121' }}>
                Number of Wrestlers: <strong>{wrestlers.length}</strong>
              </Typography>

              <Typography variant="body2" sx={{ color: '#757575', mb: 3 }}>
                Please review the wrestler data below above submitting to the tournament.
              </Typography>
            </Paper>
            
            {/* Action buttons moved here */}
            <Paper sx={{ p: 3, mb: 3, backgroundColor: '#FFFFFF', border: '1px solid #E0E0E0' }}>
              <Grid container spacing={2}>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onSubmit}
                    disabled={submitting || !schoolName.trim()}
                    startIcon={submitting ? <CircularProgress size={20} /> : null}
                  >
                    {submitting ? 'Submitting...' : 'Submit to Tournament'}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={onReset}
                    disabled={submitting}
                  >
                    Start Over
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}

        {submitted && (
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#FFFFFF', border: '1px solid #E0E0E0' }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              Submission Complete! {wrestlers.length} wrestlers from {schoolName} have been successfully added to the tournament.
            </Alert>

            <Button
              variant="contained"
              onClick={onReset}
            >
              Import Another School
            </Button>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default CsvImport;
